// src/components/admin/payroll/components/ProcessingProgress.js
// Real-time progress tracking for async payroll processing

import React, { useState, useEffect, useCallback } from 'react';
import {
    CheckCircle, XCircle, Clock, AlertTriangle, Loader,
    Users, Calculator, FileText, Lock, X
} from 'lucide-react';
import { useTheme } from '../../../../contexts/ThemeContext';
import payrollService from '../../../../services/payroll.service';

const ProcessingProgress = ({
    jobId,
    period,
    onComplete,
    onError,
    onCancel
}) => {
    const { theme } = useTheme();
    const [progress, setProgress] = useState({
        status: 'starting',
        progress: 0,
        totalEmployees: 0,
        processedEmployees: 0,
        currentStep: 'Initializing',
        eta: null,
        errors: [],
        summary: null
    });
    const [pollingError, setPollingError] = useState(null);

    // Poll for progress updates
    useEffect(() => {
        if (!jobId) return;

        let isActive = true;

        const pollProgress = async () => {
            try {
                const result = await payrollService.getProcessingProgress(jobId);

                if (!isActive) return;

                if (result.success && result.job) {
                    setProgress(result.job);
                    setPollingError(null);

                    if (result.job.status === 'completed') {
                        onComplete?.(result.job);
                    } else if (result.job.status === 'failed') {
                        onError?.(new Error(result.job.error || 'Processing failed'));
                    } else {
                        // Continue polling
                        setTimeout(pollProgress, 1000);
                    }
                }
            } catch (err) {
                if (!isActive) return;
                setPollingError(err.message);
                // Retry after delay on error
                setTimeout(pollProgress, 2000);
            }
        };

        pollProgress();

        return () => {
            isActive = false;
        };
    }, [jobId, onComplete, onError]);

    // Format time
    const formatETA = (seconds) => {
        if (!seconds || seconds < 0) return 'Calculating...';
        if (seconds < 60) return `~${seconds} seconds`;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `~${mins}m ${secs}s`;
    };

    // Step icons and labels
    const getStepInfo = (step) => {
        const steps = {
            'Initializing': { icon: Loader, label: 'Initializing payroll run', color: 'text-blue-500' },
            'Fetching employees': { icon: Users, label: 'Fetching employee data', color: 'text-blue-500' },
            'Creating payroll run': { icon: FileText, label: 'Creating payroll run', color: 'text-blue-500' },
            'Calculating salaries': { icon: Calculator, label: 'Calculating salaries', color: 'text-green-500' },
            'Finalizing': { icon: Lock, label: 'Finalizing payroll', color: 'text-purple-500' },
            'Complete': { icon: CheckCircle, label: 'Processing complete', color: 'text-green-500' },
            'Failed': { icon: XCircle, label: 'Processing failed', color: 'text-red-500' }
        };
        return steps[step] || { icon: Loader, label: step, color: 'text-gray-500' };
    };

    const stepInfo = getStepInfo(progress.currentStep);
    const StepIcon = stepInfo.icon;

    // Calculate progress bar segments
    const progressPercent = Math.min(progress.progress || 0, 100);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50" />

            {/* Modal */}
            <div className={`relative w-full max-w-lg mx-4 rounded-xl shadow-2xl overflow-hidden ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}>
                {/* Header */}
                <div className={`px-6 py-4 border-b ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {progress.status === 'completed' ? (
                                <CheckCircle className="w-6 h-6 text-green-500" />
                            ) : progress.status === 'failed' ? (
                                <XCircle className="w-6 h-6 text-red-500" />
                            ) : (
                                <Loader className="w-6 h-6 text-blue-500 animate-spin" />
                            )}
                            <div>
                                <h2 className={`text-lg font-semibold ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                    {progress.status === 'completed'
                                        ? 'Payroll Complete'
                                        : progress.status === 'failed'
                                            ? 'Processing Failed'
                                            : 'Processing Payroll'
                                    }
                                </h2>
                                <p className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    {period}
                                </p>
                            </div>
                        </div>
                        {progress.status !== 'processing' && (
                            <button
                                onClick={onCancel}
                                className={`p-2 rounded-lg ${
                                    theme === 'dark'
                                        ? 'hover:bg-gray-700 text-gray-400'
                                        : 'hover:bg-gray-100 text-gray-500'
                                }`}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Progress Content */}
                <div className="p-6">
                    {/* Main Progress Bar */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-2">
                            <span className={`text-sm font-medium ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                {progress.processedEmployees} / {progress.totalEmployees} employees
                            </span>
                            <span className={`text-sm font-bold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                {progressPercent}%
                            </span>
                        </div>
                        <div className={`h-4 rounded-full overflow-hidden ${
                            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                        }`}>
                            <div
                                className={`h-full transition-all duration-300 ${
                                    progress.status === 'failed'
                                        ? 'bg-red-500'
                                        : progress.status === 'completed'
                                            ? 'bg-green-500'
                                            : 'bg-gradient-to-r from-blue-500 to-blue-600'
                                }`}
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                    </div>

                    {/* Current Step */}
                    <div className={`flex items-center gap-3 p-4 rounded-lg mb-4 ${
                        theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                    }`}>
                        <StepIcon className={`w-5 h-5 ${stepInfo.color} ${
                            progress.status === 'processing' ? 'animate-pulse' : ''
                        }`} />
                        <div className="flex-1">
                            <p className={`text-sm font-medium ${
                                theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                                {stepInfo.label}
                            </p>
                            {progress.status === 'processing' && progress.eta && (
                                <p className={`text-xs mt-1 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                    <Clock className="w-3 h-3 inline mr-1" />
                                    Estimated time remaining: {formatETA(progress.eta)}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Steps Timeline */}
                    <div className="space-y-2 mb-4">
                        {['Fetching employees', 'Calculating salaries', 'Finalizing'].map((step, index) => {
                            const isComplete =
                                (step === 'Fetching employees' && progress.totalEmployees > 0) ||
                                (step === 'Calculating salaries' && progress.progress > 0) ||
                                (step === 'Finalizing' && progress.status === 'completed');
                            const isCurrent = progress.currentStep === step;

                            return (
                                <div
                                    key={step}
                                    className={`flex items-center gap-3 text-sm ${
                                        isComplete
                                            ? 'text-green-600'
                                            : isCurrent
                                                ? theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                                                : theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                    }`}
                                >
                                    {isComplete ? (
                                        <CheckCircle className="w-4 h-4" />
                                    ) : isCurrent ? (
                                        <Loader className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <div className={`w-4 h-4 rounded-full border-2 ${
                                            theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
                                        }`} />
                                    )}
                                    <span>{step}</span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Errors */}
                    {progress.errors && progress.errors.length > 0 && (
                        <div className={`p-3 rounded-lg mb-4 ${
                            theme === 'dark' ? 'bg-red-900/20' : 'bg-red-50'
                        }`}>
                            <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="w-4 h-4 text-red-500" />
                                <span className={`text-sm font-medium ${
                                    theme === 'dark' ? 'text-red-400' : 'text-red-700'
                                }`}>
                                    {progress.errors.length} employee(s) had errors
                                </span>
                            </div>
                            <div className="max-h-24 overflow-y-auto">
                                {progress.errors.slice(0, 5).map((err, i) => (
                                    <p key={i} className={`text-xs ${
                                        theme === 'dark' ? 'text-red-300' : 'text-red-600'
                                    }`}>
                                        {err.employeeCode}: {err.error}
                                    </p>
                                ))}
                                {progress.errors.length > 5 && (
                                    <p className={`text-xs mt-1 ${
                                        theme === 'dark' ? 'text-red-400' : 'text-red-500'
                                    }`}>
                                        +{progress.errors.length - 5} more errors
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Summary (on completion) */}
                    {progress.status === 'completed' && progress.summary && (
                        <div className={`p-4 rounded-lg ${
                            theme === 'dark' ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'
                        }`}>
                            <h3 className={`text-sm font-semibold mb-3 ${
                                theme === 'dark' ? 'text-green-400' : 'text-green-700'
                            }`}>
                                Processing Summary
                            </h3>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <p className={`text-xs ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>Total Employees</p>
                                    <p className={`text-lg font-bold ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>{progress.summary.totalEmployees}</p>
                                </div>
                                <div>
                                    <p className={`text-xs ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>Total Gross</p>
                                    <p className={`text-lg font-bold ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>₹{(progress.summary.totalGross / 100000).toFixed(2)}L</p>
                                </div>
                                <div>
                                    <p className={`text-xs ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>Total Deductions</p>
                                    <p className={`text-lg font-bold ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>₹{(progress.summary.totalDeductions / 100000).toFixed(2)}L</p>
                                </div>
                                <div>
                                    <p className={`text-xs ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>Total Net</p>
                                    <p className={`text-lg font-bold text-green-600`}>
                                        ₹{(progress.summary.totalNet / 100000).toFixed(2)}L
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Polling Error */}
                    {pollingError && (
                        <div className={`p-3 rounded-lg flex items-center gap-2 ${
                            theme === 'dark' ? 'bg-yellow-900/20' : 'bg-yellow-50'
                        }`}>
                            <AlertTriangle className="w-4 h-4 text-yellow-500" />
                            <p className={`text-sm ${
                                theme === 'dark' ? 'text-yellow-400' : 'text-yellow-700'
                            }`}>
                                Connection issue, retrying...
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className={`px-6 py-4 border-t ${
                    theme === 'dark' ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'
                }`}>
                    {progress.status === 'completed' ? (
                        <button
                            onClick={onCancel}
                            className="w-full px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
                        >
                            Done
                        </button>
                    ) : progress.status === 'failed' ? (
                        <div className="flex gap-3">
                            <button
                                onClick={onCancel}
                                className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                Close
                            </button>
                        </div>
                    ) : (
                        <p className={`text-sm text-center ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Processing in progress. Please wait...
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProcessingProgress;
